import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface MeetingMinute {
  id: number;
  title: string;
  meetingType?: string | null;
  meetingDate?: string | null;
  participants?: string | null;
  notes?: string | null;
  workId?: number | null;
  workName?: string | null;
  created: string;
  lastModified: string;
}

export interface MeetingMinutePayload {
  title: string;
  meetingType?: string | null;
  meetingDate?: string | null;
  participants?: string | null;
  notes?: string | null;
  workId?: number | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

export function getMeetingMinutes(
  workId?: number,
): Promise<MeetingMinute[]> {
  const query =
    workId !== undefined && workId !== null && Number.isFinite(workId)
      ? `?workId=${workId}`
      : "";
  return request<MeetingMinute[]>(`/api/meetingminutes${query}`);
}

export function getMeetingMinuteById(
  minuteId: number,
): Promise<MeetingMinute> {
  return request<MeetingMinute>(`/api/meetingminutes/${minuteId}`);
}

export function createMeetingMinute(
  payload: MeetingMinutePayload,
): Promise<MeetingMinute> {
  return request<MeetingMinute>("/api/meetingminutes", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateMeetingMinute(
  minuteId: number,
  payload: MeetingMinutePayload,
): Promise<MeetingMinute> {
  return request<MeetingMinute>(`/api/meetingminutes/${minuteId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteMeetingMinute(minuteId: number): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/meetingminutes/${minuteId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
